import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../../../assets/images/logo.png";
import { apiHandler } from "../../../api/apihandler";
import { LATEST_RECIPES } from "../../../api/constants";
import { apiSucceeded } from "../../../views/dashboard/feed/feedUtils";
import "./nemonto-landing-page.css";

const cuisines = ["Bengali", "Italian", "South Asian", "Mexican", "BBQ", "French", "East Asian"];

const FEED_ICONS = ["bowl", "steam", "tray", "plate", "orb"];
const FEED_ACCENTS = [
  "linear-gradient(150deg,#E8916F,#B23D18)",
  "linear-gradient(150deg,#6FCBA4,#0D6B4E)",
  "linear-gradient(150deg,#9890DF,#534AB7)",
  "linear-gradient(150deg,#FCD58E,#BA7517)",
  "linear-gradient(150deg,#E8693D,#993C1D)",
];

const fallbackFeedCards = [
  {
    accent: "linear-gradient(150deg,#E8916F,#B23D18)",
    avatar: "#F0997B",
    author: "Maria K.",
    cuisine: "Italian",
    dish: "Cacio e Pepe - nonna's version",
    meta: "15 min · 47 made it",
    icon: "bowl",
  },
  {
    accent: "linear-gradient(150deg,#6FCBA4,#0D6B4E)",
    avatar: "#9FE1CB",
    author: "Priya R.",
    cuisine: "South Asian",
    dish: "Dal Tadka with ghee drizzle",
    meta: "25 min · 89 made it",
    icon: "steam",
  },
  {
    accent: "linear-gradient(150deg,#9890DF,#534AB7)",
    avatar: "#AFA9EC",
    author: "James L.",
    cuisine: "BBQ",
    dish: "Low-and-slow smoked brisket",
    meta: "8 hr · 34 made it",
    icon: "tray",
  },
  {
    accent: "linear-gradient(150deg,#FCD58E,#BA7517)",
    avatar: "#FAC775",
    author: "Sofia M.",
    cuisine: "Bakery",
    dish: "Rosemary sourdough, no knead",
    meta: "18 hr · 61 made it",
    icon: "plate",
  },
  {
    accent: "linear-gradient(150deg,#E8693D,#993C1D)",
    avatar: "#F5C4B3",
    author: "Chen W.",
    cuisine: "East Asian",
    dish: "Sichuan mapo tofu, from scratch",
    meta: "30 min · 52 made it",
    icon: "orb",
  },
];

const steps = [
  {
    step: "One",
    title: "Post your recipe",
    copy: "Film it, write the ingredients, set the cook time. It lands on the feed looking like a real dinner invite, not a database entry.",
    tone: "#D85A30",
    background: "rgba(216,90,48,0.12)",
    icon: "pan",
  },
  {
    step: "Two",
    title: "Someone makes it",
    copy: "They tap 'Made it?' and post their version - nailed it, almost, or their own twist. Every reply is a real cooking session, not a comment.",
    tone: "#1D9E75",
    background: "rgba(29,158,117,0.12)",
    icon: "check",
  },
  {
    step: "Three",
    title: "The recipe grows up",
    copy: "Forty-seven versions later, one dish has a whole community behind it. That thread is more useful than any star rating.",
    tone: "#BA7517",
    background: "rgba(250,199,117,0.22)",
    icon: "award",
  },
];

const creatorPerks = [
  "Permanent founding badge visible on your profile forever, even at 5 million users.",
  "Free Pro, for life - grandfathered in before we ever charge a cent.",
  "First right to host challenges - you help define what a Nemonto challenge feels like.",
  "Weekly spotlight - we promote you on our own channels, not the other way around.",
];

const testimonials = [
  {
    quote: "I posted my grandmother's recipe and within a week, six strangers had cooked it and told me their version. That never happens on Instagram.",
    name: "Maria K.",
    tag: "Founding Creator",
    avatar: "#F0997B",
  },
  {
    quote: "I won a challenge against eighty other entries and the host actually messaged me herself. It felt like winning something real.",
    name: "Priya R.",
    tag: "Challenge Winner",
    avatar: "#9FE1CB",
  },
  {
    quote: "My feed isn't full of ads for kitchen gadgets I don't need. It's just food people actually made, in their actual kitchens.",
    name: "James L.",
    tag: "Home Cook",
    avatar: "#AFA9EC",
  },
];

const FoodGlyph = ({ type }) => {
  const common = { className: "nemonto-food-icon", "aria-hidden": true };

  if (type === "steam") {
    return (
      <svg viewBox="0 0 100 100" {...common}>
        <circle cx="35" cy="42" r="10" fill="#fff" opacity="0.9" />
        <circle cx="58" cy="40" r="8" fill="#fff" opacity="0.7" />
        <circle cx="64" cy="54" r="6" fill="#fff" opacity="0.6" />
      </svg>
    );
  }

  if (type === "tray") {
    return (
      <svg viewBox="0 0 100 100" {...common}>
        <rect x="22" y="34" width="56" height="42" rx="8" fill="#fff" opacity="0.6" />
        <rect x="28" y="24" width="44" height="13" rx="6" fill="#fff" opacity="0.75" />
      </svg>
    );
  }

  if (type === "plate") {
    return (
      <svg viewBox="0 0 100 100" {...common}>
        <path d="M24 48 Q24 70 50 70 Q76 70 76 48 Z" fill="#fff" opacity="0.85" />
      </svg>
    );
  }

  if (type === "orb") {
    return (
      <svg viewBox="0 0 100 100" {...common}>
        <circle cx="50" cy="50" r="28" fill="#fff" opacity="0.3" />
        <circle cx="50" cy="50" r="16" fill="#fff" opacity="0.55" />
        <circle cx="50" cy="50" r="6" fill="#fff" opacity="0.9" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 100 100" {...common}>
      <ellipse cx="50" cy="74" rx="30" ry="6" fill="#993C1D" opacity="0.4" />
      <path d="M20 50 Q20 78 50 78 Q80 78 80 50 Z" fill="#fff" opacity="0.85" />
      <path d="M18 47 Q18 53 23 53 L77 53 Q82 53 82 47 Q82 38 50 38 Q18 38 18 47 Z" fill="#fff" />
    </svg>
  );
};

const StepIcon = ({ kind, tone }) => {
  if (kind === "check") {
    return (
      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path d="M4 12l5 5L20 6" fill="none" stroke={tone} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (kind === "award") {
    return (
      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4z" fill="none" stroke={tone} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path d="M5 12c0-4 3-7 7-7s7 3 7 7-3 7-7 7" fill="none" stroke={tone} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M9 19l-2 2M15 19l2 2" fill="none" stroke={tone} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

const NemontoLandingPage = () => {
  const [feedCards, setFeedCards] = useState(fallbackFeedCards);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiHandler(LATEST_RECIPES, "GET");
        if (cancelled || !apiSucceeded(res)) return;
        const raw = res?.data?.data?.recipes || [];
        if (raw.length === 0) return;
        setFeedCards(raw.map((d, i) => {
          const firstMedia = d.user_post_images?.[0] || null;
          const isVideo = firstMedia?.media_type === 1;
          const image = firstMedia ? (isVideo ? (firstMedia.thumb || null) : firstMedia.image_path) : null;
          return {
            id: d.id,
            accent: FEED_ACCENTS[i % FEED_ACCENTS.length],
            icon: FEED_ICONS[i % FEED_ICONS.length],
            avatarUrl: d.user?.user_setting?.photo || null,
            author: `${d.user?.first_name || ''} ${d.user?.last_name || ''}`.trim() || 'A cook',
            cuisine: d.cuisine_type?.cuisine?.name || '',
            dish: d.title || 'Untitled recipe',
            image,
            isVideo,
            meta: [d.cook_time ? `${d.cook_time} min` : null, d.serving_for ? `Serves ${d.serving_for}` : null].filter(Boolean).join(' · '),
          };
        }));
      } catch (e) { console.error(e); }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="nemonto-landing">
      <nav className="nemonto-nav">
        <div className="nemonto-nav-inner">
          <Link to="/" aria-label="Nemonto home">
            <img src={logo} alt="Nemonto" className="nemonto-logo" />
          </Link>

          <div className="nemonto-links">
            <a href="#table">For foodies</a>
            <a href="#challenges">Challenges</a>
            <a href="#creators">For creators</a>
            <a href="#stories">Stories</a>
          </div>

          <div className="nemonto-actions">
            <Link to="/auth/sign-in" className="nemonto-pill-outline">Sign in</Link>
            <Link to="/auth/sign-up" className="nemonto-pill">Sign up</Link>
          </div>
        </div>
      </nav>

      <header className="nemonto-hero">
        <div className="nemonto-steam-bg" />
        <div className="nemonto-wrap nemonto-hero-grid">
          <div>
            <span className="nemonto-kicker"><span className="nemonto-kicker-dot" />Now seating in New York City</span>
            <h1 className="nemonto-title nemonto-serif">You&apos;re invited<br />to <em>the table.</em></h1>
            <p className="nemonto-subtitle">Nemonto comes from a Bengali word for dinner invitation. Post the recipe. Someone cooks it. They post their version back. That&apos;s the whole app - and it&apos;s more fun than it sounds.</p>
            <div className="nemonto-hero-ctas">
              <a href="#get" className="nemonto-button nemonto-button-primary">Accept the invitation</a>
              <a href="#table" className="nemonto-button nemonto-button-ghost">See it in action</a>
            </div>
            <div className="nemonto-proof">
              <div className="nemonto-avatar-row">
                <span style={{ background: "#F0997B" }} />
                <span style={{ background: "#9FE1CB" }} />
                <span style={{ background: "#AFA9EC" }} />
                <span style={{ background: "#FAC775" }} />
              </div>
              <p><strong>300+ home cooks</strong> already pulled up a chair</p>
            </div>
          </div>

          <div className="nemonto-visual" aria-hidden="true">
            <div className="nemonto-bowl" />

            <div className="nemonto-card nemonto-card-one">
              <div className="nemonto-thumb" style={{ background: "linear-gradient(135deg,#F0997B,#D85A30)" }}>
                <FoodGlyph type="bowl" />
              </div>
              <p className="nemonto-card-title">Cacio e Pepe - nonna&apos;s version</p>
              <p className="nemonto-card-meta">by Maria K. · 47 made it</p>
            </div>

            <div className="nemonto-card nemonto-card-two">
              <div className="nemonto-thumb" style={{ background: "linear-gradient(135deg,#9FE1CB,#1D9E75)" }}>
                <FoodGlyph type="steam" />
              </div>
              <p className="nemonto-card-title">Dal Tadka with ghee drizzle</p>
              <p className="nemonto-card-meta">by Priya R. · 89 hearts</p>
            </div>

            <div className="nemonto-badge">🔥 Nailed it - posted 2 min ago</div>
          </div>
        </div>
      </header>

      <section className="nemonto-strip">
        <div className="nemonto-wrap nemonto-strip-inner">
          <p className="nemonto-strip-label">Every table has a seat for</p>
          <div className="nemonto-cuisine-row nemonto-serif">
            {cuisines.map((cuisine) => (
              <span key={cuisine}>{cuisine}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="nemonto-table" id="table">
        <div className="nemonto-wrap">
          <div className="nemonto-section-head">
            <span className="nemonto-section-label">The feed</span>
            <h2 className="nemonto-section-title">Pull up a chair.<br />Everyone&apos;s <em>cooking something.</em></h2>
            <p className="nemonto-section-copy">No filters, no algorithm games. Just recipes from real people, and proof that someone else actually made them.</p>
          </div>
        </div>

        <div className="nemonto-scroll">
          {feedCards.map((card) => (
            <Link to="/auth/sign-up" className="nemonto-feed-card" key={card.id ?? card.dish}>
              <div className="nemonto-feed-top">
                {card.avatarUrl ? (
                  <img src={card.avatarUrl} alt="" className="nemonto-feed-avatar" />
                ) : (
                  <div className="nemonto-feed-avatar" style={{ background: card.avatar || "#F0997B" }} />
                )}
                <p className="nemonto-feed-who"><strong>{card.author}</strong> · {card.cuisine}</p>
              </div>
              <div className="nemonto-thumb nemonto-feed-thumb" style={{ background: card.accent }}>
                {card.image ? (
                  <img src={card.image} alt="" className="nemonto-feed-thumb-img" />
                ) : (
                  <FoodGlyph type={card.icon} />
                )}
              </div>
              <p className="nemonto-feed-dish">{card.dish}</p>
              <p className="nemonto-feed-meta">{card.meta}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="nemonto-how">
        <div className="nemonto-wrap">
          <div className="nemonto-section-head">
            <span className="nemonto-section-label">How it works</span>
            <h2 className="nemonto-section-title">Three things happen.<br />That&apos;s <em>the whole loop.</em></h2>
          </div>

          <div className="nemonto-how-grid">
            {steps.map((step) => (
              <article className="nemonto-how-card" key={step.title}>
                <span className="nemonto-how-step" style={{ color: step.tone }}>{step.step}</span>
                <div className="nemonto-how-icon" style={{ background: step.background }}>
                  <StepIcon kind={step.icon} tone={step.tone} />
                </div>
                <p className="nemonto-how-title">{step.title}</p>
                <p className="nemonto-how-copy">{step.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="nemonto-wrap nemonto-banner-wrap" id="challenges">
        <div className="nemonto-banner">
          <div>
            <span className="nemonto-kicker"><span className="nemonto-kicker-dot" />Hosted by real creators</span>
            <h2 className="nemonto-banner-title">Three days.<br />Three ingredients.<br />One <em>winner.</em></h2>
            <p className="nemonto-banner-copy">Creators set the rules - the ingredients, the cuisine, the clock. You cook. They personally pick the winner. No algorithm decides who&apos;s best at dinner.</p>
            <div className="nemonto-challenge-list">
              <div className="nemonto-challenge-list-item"><div className="nemonto-check" />A real person judges, not a like count</div>
              <div className="nemonto-challenge-list-item"><div className="nemonto-check" />Winner badges live on your profile, permanently</div>
              <div className="nemonto-challenge-list-item"><div className="nemonto-check" />New challenge dropping every few days</div>
            </div>
            <a href="#get" className="nemonto-button nemonto-button-primary">See this week&apos;s challenge</a>
          </div>

          <div className="nemonto-challenge-card">
            <span className="nemonto-live"><span />LIVE NOW</span>
            <p className="nemonto-challenge-heading">Pasta with 3 ingredients only</p>
            <div className="nemonto-chips">
              <span>Pasta</span>
              <span>Eggs</span>
              <span>Pecorino</span>
            </div>
            <div className="nemonto-challenge-stats">
              <div className="nemonto-stat"><strong>84</strong><span>entries</span></div>
              <div className="nemonto-stat"><strong>30m</strong><span>max cook</span></div>
              <div className="nemonto-stat"><strong>47h</strong><span>remaining</span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="nemonto-creator" id="creators">
        <div className="nemonto-wrap nemonto-creator-grid">
          <div className="nemonto-certificate">
            <small>Nemonto · Founding Creator</small>
            <div className="nemonto-certificate-bowl" />
            <p className="nemonto-certificate-name">Maria K.</p>
            <p className="nemonto-certificate-copy">was among the first 20 creators to join.<br />Dinner invitation accepted.</p>
            <span className="nemonto-certificate-badge">Founding Creator #004</span>
          </div>

          <div>
            <span className="nemonto-section-label">For creators</span>
            <h2 className="nemonto-section-title">Bring your followers.<br />Keep <em>the recognition.</em></h2>
            <p className="nemonto-section-copy">The first 20 creators in every city get a permanent badge, lifetime Pro access, and first right to host challenges. Not a partnership deck - just a seat saved for you.</p>
            <ul className="nemonto-list">
              {creatorPerks.map((perk) => (
                <li key={perk}><span>•</span><span>{perk}</span></li>
              ))}
            </ul>
            <Link to="/auth/sign-up" className="nemonto-button nemonto-button-ghost">Apply as a founding creator</Link>
          </div>
        </div>
      </section>

      <section className="nemonto-quotes" id="stories">
        <div className="nemonto-wrap">
          <div className="nemonto-section-head">
            <span className="nemonto-section-label">From the table</span>
            <h2 className="nemonto-section-title">People are actually <em>cooking.</em></h2>
          </div>
          <div className="nemonto-quote-grid">
            {testimonials.map((item) => (
              <article className="nemonto-quote-card" key={item.name}>
                <p className="nemonto-quote-copy">"{item.quote}"</p>
                <div className="nemonto-quote-meta">
                  <div className="nemonto-quote-avatar" style={{ background: item.avatar }} />
                  <div>
                    <p className="nemonto-card-title">{item.name}</p>
                    <p className="nemonto-quote-tag">{item.tag}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="nemonto-cta" id="get">
        <div className="nemonto-wrap">
          <h2 className="nemonto-section-title" style={{ fontSize: "clamp(34px,5vw,58px)", marginBottom: 22 }}>Dinner&apos;s ready.<br /><em>Pull up a chair.</em></h2>
          <p className="nemonto-cta-copy">Free to join. No algorithm deciding what you see. Just recipes, made by people, for people.</p>
          <div className="nemonto-store-row">
            <Link to="/auth/sign-up" className="nemonto-store nemonto-store-primary"><small>Join the</small><strong>Waitlist</strong></Link>
            <Link to="/auth/sign-in" className="nemonto-store nemonto-store-ghost"><small>Already invited?</small><strong>Sign in</strong></Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default NemontoLandingPage;